/**
 * Copy text to clipboard. Pass a function returning Promise<string> for iOS Safari
 * compatibility when async operations (like API calls) are needed before copying.
 */
export async function copyToClipboard(text: string | (() => Promise<string>)): Promise<void> {
  if (typeof text === 'function') {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      const item = new ClipboardItem({
        'text/plain': text().then((t) => new Blob([t], { type: 'text/plain' }))
      })
      await navigator.clipboard.write([item])
    } else {
      const resolvedText = await text()
      await fallbackCopy(resolvedText)
    }
    return
  }

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    const item = new ClipboardItem({
      'text/plain': new Blob([text], { type: 'text/plain' })
    })
    await navigator.clipboard.write([item])
  } else {
    await fallbackCopy(text)
  }
}

async function fallbackCopy(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch {
      // Fall through to execCommand
    }
  }

  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.top = '0'
  textArea.style.left = '0'
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'

  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    const successful = document.execCommand('copy')
    if (!successful) {
      throw new Error('execCommand copy failed')
    }
  } finally {
    document.body.removeChild(textArea)
  }
}

/**
 * Build a client portal URL with optional access token.
 */
export function buildPortalUrl(path: string, accessToken?: string | null): string {
  const baseUrl = `${window.location.origin}${path}`
  if (accessToken?.trim()) {
    return `${baseUrl}?access_token=${accessToken}`
  }
  return baseUrl
}
